import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Verification status + one-time signing-secret reveal for an outbound webhook
 * destination (CASA 7.1.2 / 7.3.2 — plan WP-5 W10).
 *
 * Shared by the form forwarder editor (generic API forwarders) and the campaign
 * "Webhook" step. The server owns every state field; this component only shows
 * them and calls the two admin endpoints the caller wires in:
 *
 *   onVerify()        → resolves to the new state ({verification, verified_at, ...})
 *   onReveal(rotate)  → resolves to the signing secret string (shown once)
 *
 * State only applies to the URL it was issued for (`verified_url`). While the
 * URL field holds anything else, the destination is "checked on save".
 */
const TIERS = {
    verified_echo: {
        label: 'Verified',
        className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        hint: 'The endpoint answered the verification challenge.',
    },
    verified_platform: {
        label: 'Platform',
        className: 'bg-blue-50 text-blue-700 border-blue-200',
        hint: 'An integration-platform URL (Zapier, Make, n8n, Slack, ...) — the platform issued it to your account.',
    },
    legacy: {
        label: 'Unverified',
        className: 'bg-amber-50 text-amber-700 border-amber-200',
        hint: 'Saved before destinations were verified. It keeps delivering; verify it to confirm you own the endpoint.',
    },
};

/** The saved state for this config's current URL, or null if the URL changed. */
export function savedDestinationState(config) {
    const url = (config?.url || '').trim();
    if (!url || !config?.verification) return null;
    if ((config.verified_url || '').trim() !== url) return null;
    return TIERS[config.verification] ? config : null;
}

function errorText(err, fallback) {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    return err?.message || fallback;
}

export default function WebhookDestinationStatus({ config, onStateChange, onVerify, onReveal }) {
    const [busy, setBusy] = useState(null);      // 'verify' | 'reveal' | 'rotate' | null
    const [error, setError] = useState(null);
    const [secret, setSecret] = useState(null);  // only ever held in memory, never in config
    const [copied, setCopied] = useState(false);

    const url = (config?.url || '').trim();
    if (!url) return null;

    const state = savedDestinationState(config);
    if (!state) {
        return (
            <p data-testid="webhook-status-pending" className="text-[11px] text-stone-500">
                Verified when you save: the endpoint must answer a verification POST with its
                {' '}<code>challenge</code> value (https only). Zapier, Make and n8n catch-hook URLs verify automatically.
            </p>
        );
    }

    const tier = TIERS[state.verification];

    const verify = async () => {
        if (!onVerify) return;
        setBusy('verify'); setError(null);
        try {
            const next = await onVerify();
            onStateChange && onStateChange({ ...config, ...next });
        } catch (err) {
            setError(errorText(err, 'Verification failed'));
        } finally {
            setBusy(null);
        }
    };

    const reveal = async (rotate) => {
        if (!onReveal) return;
        if (rotate && !window.confirm('Rotate the signing secret? The current secret stops working immediately; update your endpoint with the new one.')) return;
        setBusy(rotate ? 'rotate' : 'reveal'); setError(null); setCopied(false);
        try {
            const value = await onReveal(rotate);
            setSecret(value);
            onStateChange && onStateChange({ ...config, signing_secret_set: true, signing_secret_revealed: true });
        } catch (err) {
            setError(errorText(err, 'Could not reveal the signing secret'));
        } finally {
            setBusy(null);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(secret);
            setCopied(true);
        } catch {
            setCopied(false);
        }
    };

    return (
        <div data-testid="webhook-status" className="rounded-lg border border-stone-200 bg-stone-50 p-2.5 space-y-2">
            <div className="flex items-center justify-between gap-2">
                <span
                    data-testid="webhook-status-chip"
                    title={tier.hint}
                    className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium border rounded-full ${tier.className}`}
                >
                    {tier.label}
                </span>
                {state.verification === 'legacy' && onVerify && (
                    <button
                        type="button"
                        data-testid="webhook-verify"
                        onClick={verify}
                        disabled={!!busy}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium border border-stone-300 rounded-md bg-white hover:border-stone-400 disabled:opacity-50"
                    >
                        {busy === 'verify' && <Loader2 className="w-3 h-3 animate-spin" />}
                        Verify
                    </button>
                )}
            </div>
            <p className="text-[11px] text-stone-500">{tier.hint}</p>
            {state.insecure_scheme && (
                <p className="text-[11px] text-amber-700">
                    This URL uses http://. Deliveries are sent over https:// instead; change the URL to https:// to verify it.
                </p>
            )}

            {state.signing_secret_set && onReveal && (
                <div className="border-t border-stone-200 pt-2 space-y-1.5">
                    <div className="text-[11px] text-stone-600">
                        Deliveries are signed: <code>X-NurtureCRM-Signature: t=&lt;unix&gt;,v1=&lt;hex&gt;</code>,
                        HMAC-SHA256 of <code>{'"{t}.{body}"'}</code> with this destination's secret.
                    </div>
                    {secret ? (
                        <div className="space-y-1">
                            <div className="flex items-center gap-1.5">
                                <code data-testid="webhook-secret" className="flex-1 min-w-0 truncate px-2 py-1 text-[11px] bg-white border border-stone-200 rounded">
                                    {secret}
                                </code>
                                <button type="button" onClick={copy} className="px-2 py-1 text-[11px] border border-stone-300 rounded-md bg-white hover:border-stone-400">
                                    {copied ? 'Copied' : 'Copy'}
                                </button>
                            </div>
                            <p className="text-[11px] text-amber-700">Shown once. Store it now; it cannot be displayed again.</p>
                        </div>
                    ) : state.signing_secret_revealed ? (
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-[11px] text-stone-500">Signing secret already revealed.</span>
                            <button
                                type="button"
                                data-testid="webhook-rotate"
                                onClick={() => reveal(true)}
                                disabled={!!busy}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-stone-300 rounded-md bg-white hover:border-stone-400 disabled:opacity-50"
                            >
                                {busy === 'rotate' && <Loader2 className="w-3 h-3 animate-spin" />}
                                Rotate
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            data-testid="webhook-reveal"
                            onClick={() => reveal(false)}
                            disabled={!!busy}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium border border-stone-300 rounded-md bg-white hover:border-stone-400 disabled:opacity-50"
                        >
                            {busy === 'reveal' && <Loader2 className="w-3 h-3 animate-spin" />}
                            Reveal signing secret
                        </button>
                    )}
                </div>
            )}

            {error && <p data-testid="webhook-status-error" className="text-[11px] text-red-600">{error}</p>}
        </div>
    );
}
